/**
 * Keep wheel/trackpad deltas inside a floating overlay (popover, dropdown,
 * type menu). Without this, overscroll at the list edge — or a wheel that
 * hits the panel chrome — chains to the table and then the page.
 */

export function isOverlayWheelTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  return Boolean(
    target.closest('[popover], .nx-popover-panel, .nx-dropdown-popover, .nx-menu-panel')
  );
}

function canScrollY(el: HTMLElement): boolean {
  const oy = getComputedStyle(el).overflowY;
  return /(auto|scroll)/.test(oy) && el.scrollHeight > el.clientHeight + 1;
}

function canScrollX(el: HTMLElement): boolean {
  const ox = getComputedStyle(el).overflowX;
  return /(auto|scroll)/.test(ox) && el.scrollWidth > el.clientWidth + 1;
}

function nearestOverlayScroller(from: HTMLElement, root: HTMLElement): HTMLElement | null {
  let node: HTMLElement | null = from;
  while (node && root.contains(node)) {
    if (canScrollY(node) || canScrollX(node)) return node;
    node = node.parentElement;
  }
  return canScrollY(root) || canScrollX(root) ? root : null;
}

/** Stop the event leaving the overlay; preventDefault when the list cannot move. */
export function containOverlayWheel(event: WheelEvent, root: EventTarget | null): void {
  event.stopPropagation();
  const host = root instanceof HTMLElement ? root : null;
  if (!host) {
    event.preventDefault();
    return;
  }
  const start = event.target instanceof HTMLElement ? event.target : host;
  const scroller = nearestOverlayScroller(start, host);
  if (!scroller) {
    event.preventDefault();
    return;
  }
  const dy = event.deltaY;
  const dx = event.deltaX;
  const atTop = scroller.scrollTop <= 0;
  const atBottom = scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 1;
  const atLeft = scroller.scrollLeft <= 0;
  const atRight = scroller.scrollLeft + scroller.clientWidth >= scroller.scrollWidth - 1;
  const blockedY = dy < 0 ? atTop : dy > 0 ? atBottom : false;
  const blockedX = dx < 0 ? atLeft : dx > 0 ? atRight : false;
  if (Math.abs(dy) >= Math.abs(dx) ? blockedY : blockedX) {
    event.preventDefault();
  }
}
